import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
  SxProps,
  Theme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { isZip, streamZipImages, createImageSummary, extractImageFromZip, ImageFileSummary } from './imageFileUtils';
import { Button } from '@baseComponents';
import { Toast } from '@components';
import { useUser } from '@hooks/useUser';
import { useAgency } from '@hooks/useAgency';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import {
  fetchAgencyOfficials,
  updateOfficialImage,
  updateOfficial,
  OfficialData
} from '@networkRequest/officialsService';
import ImagesPanel from './ImagesPanel';
import UsersPanel, { MatchFilter } from './UsersPanel';
import Utils from '../../utils/utils';

interface ImportImagesDialogProps {
  open: boolean;
  onClose: () => void;
}

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 10;

// The MUI Dialog paper is sized via the `style` prop (plain CSS, not `sx`), so it
// lives outside the theme-aware `styles` object below.
const DIALOG_PAPER_STYLE: React.CSSProperties = {
  borderRadius: 8,
  width: 'calc(100vw - 48px)',
  maxWidth: 'calc(100vw - 48px)',
  height: 'calc(100vh - 72px)',
  maxHeight: 'calc(100vh - 72px)'
};

const ImportImagesDialog = ({ open, onClose }: ImportImagesDialogProps) => {
  const theme = useTheme();
  const { user } = useUser();
  const { agency } = useAgency();
  const [images, setImages] = useState<ImageFileSummary[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [users, setUsers] = useState<OfficialData[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [gridPagination, setGridPagination] = useState({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const [pagination, setPagination] = useState({
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
    total_count: 0
  });
  const [counts, setCounts] = useState({ all: 0, withPhoto: 0, without: 0 });
  const [countsLoading, setCountsLoading] = useState(false);
  const [matchFilter, setMatchFilter] = useState<MatchFilter>('unmatched');
  const [searchText, setSearchText] = useState('');
  const [selectedImageName, setSelectedImageName] = useState<string | null>(null);
  const [draggedImageName, setDraggedImageName] = useState<string | null>(null);
  const [matchingUserIds, setMatchingUserIds] = useState<number[]>([]);
  const [matchingImageNames, setMatchingImageNames] = useState<string[]>([]);
  const [mapError, setMapError] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);

  // Tracks the latest in-flight request so out-of-order responses are ignored.
  const usersRequestIdRef = useRef(0);
  const countsRequestIdRef = useRef(0);
  // Photos mapped during this session, keyed by official id, so unlinking a
  // user returns the original photo to the left panel. Backend-sourced photos
  // (loaded with the user) aren't here, so unlinking those restores nothing.
  const mappedImagesRef = useRef<Map<number, ImageFileSummary>>(new Map());

  // Debounce the query inputs together so rapid typing/paging fires one request.
  const fetchParams = useMemo(
    () => ({ searchText, gridPagination, matchFilter }),
    [searchText, gridPagination, matchFilter]
  );
  const debouncedParams = useDebouncedValue(fetchParams, 300);

  // Loads the current page of users for the active tab. `silent` skips the loading
  // state and keeps the existing rows on screen until fresh data arrives — used
  // for the unobtrusive refetch after a match (to refresh the pagination count).
  const loadUsers = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!agency?.id || !user?.accessToken) return;
      const { searchText: query, gridPagination: grid, matchFilter: filter } = debouncedParams;
      const requestId = ++usersRequestIdRef.current;
      if (!silent) {
        setUsersLoading(true);
        setUsersError('');
      }
      try {
        const offset = grid.page * grid.pageSize;
        // All → no has_image filter; Unmapped → has_image=false; Mapped → has_image=true.
        const hasImage =
          filter === 'matched' ? ['true'] : filter === 'unmatched' ? ['false'] : undefined;
        const {
          data,
          pagination: paginationData,
          error
        } = await fetchAgencyOfficials({
          agencyId: agency.id,
          accessToken: user.accessToken,
          limit: grid.pageSize,
          offset,
          hasImage,
          query: query || undefined
        });
        if (requestId !== usersRequestIdRef.current) return;
        if (error || !data || !paginationData) {
          if (!silent) setUsersError('Failed to load users.');
          return;
        }
        setUsers(data);
        setPagination(paginationData);
      } catch {
        if (requestId === usersRequestIdRef.current && !silent) {
          setUsersError('Failed to load users.');
        }
      } finally {
        if (requestId === usersRequestIdRef.current && !silent) setUsersLoading(false);
      }
    },
    [agency?.id, user?.accessToken, debouncedParams]
  );

  useEffect(() => {
    if (!open) return;
    loadUsers();
  }, [open, loadUsers]);

  // Fetches the totals for all three tabs at once (limit 1 — we only need the
  // pagination counts) for the summary line. Independent of the active tab.
  // `silent` skips the loading state so background refreshes (after a map/unlink)
  // update the numbers in place rather than flashing the spinner.
  const loadCounts = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!agency?.id || !user?.accessToken) return;
      const requestId = ++countsRequestIdRef.current;
      if (!silent) setCountsLoading(true);
      const base = { agencyId: agency.id, accessToken: user.accessToken, limit: 1, offset: 0 };
      try {
        const [all, withPhoto, without] = await Promise.all([
          fetchAgencyOfficials(base),
          fetchAgencyOfficials({ ...base, hasImage: ['true'] }),
          fetchAgencyOfficials({ ...base, hasImage: ['false'] })
        ]);
        if (requestId !== countsRequestIdRef.current) return;
        setCounts({
          all: all.pagination?.total_count ?? 0,
          withPhoto: withPhoto.pagination?.total_count ?? 0,
          without: without.pagination?.total_count ?? 0
        });
      } finally {
        if (requestId === countsRequestIdRef.current && !silent) setCountsLoading(false);
      }
    },
    [agency?.id, user?.accessToken]
  );

  useEffect(() => {
    if (!open) return;
    loadCounts();
  }, [open, loadCounts]);

  // Warn before leaving the page while unmapped photos are still in the left
  // panel — they only live in memory and would be lost. `beforeunload` covers a
  // real page unload (URL change, refresh, tab close) with the native prompt.
  useEffect(() => {
    if (!open || images.length === 0) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Legacy assignment required by some browsers to show the prompt.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [open, images.length]);

  // The back button navigates within the SPA (a popstate, not a page unload), so
  // `beforeunload` never fires for it. Intercept popstate instead: push a sentinel
  // entry while guarding, and on Back show the native confirm — only letting the
  // navigation through if the user accepts, otherwise re-arming the sentinel.
  useEffect(() => {
    if (!open || images.length === 0) return;
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      const confirmLeave = window.confirm(
        'You have unmapped photos that will be lost if you leave. Leave anyway?'
      );
      if (confirmLeave) {
        window.removeEventListener('popstate', handlePopState);
        window.history.back();
      } else {
        // Stay on the page: re-push the sentinel so the next Back press is caught.
        window.history.pushState(null, '', window.location.href);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [open, images.length]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setIsProcessing(true);
    await Utils.yieldToBrowser();
    try {
      for (const file of acceptedFiles) {
        if (isZip(file)) {
          try {
            await streamZipImages(file, async (name, sizeBytes, blob) => {
              try {
                const image = await createImageSummary(name, sizeBytes, blob, { type: 'zip', zipFile: file });
                setImages((prev) => (prev.some((e) => e.name === name) ? prev : [...prev, image]));
              } catch {
                // skip images that fail thumbnail generation
              }
            });
          } catch {
            // skip unreadable ZIPs
          }
        } else {
          try {
            const image = await createImageSummary(file.name, file.size, file, { type: 'file', file });
            setImages((prev) => (prev.some((e) => e.name === file.name) ? prev : [...prev, image]));
          } catch {
            // skip images that fail thumbnail generation
          }
        }
      }
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const styles = useMemo(
    () =>
      ({
        closeButton: {
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500]
        },
        closeIcon: {
          fontSize: 32,
          color: theme.palette.grey[600]
        },
        dialogContent: {
          padding: 3,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden'
        },
        // Fixed header
        header: {
          flexShrink: 0,
          paddingBottom: 3
        },
        headerTitle: { mb: 1 },
        // Side-by-side panels, filling the space between header and footer so each
        // can scroll within it. Stacks vertically on narrow widths.
        panels: {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          gap: 2,
          pb: 3,
          '@media (max-width: 800px)': {
            flexDirection: 'column'
          }
        },
        footer: {
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1
        }
      }) satisfies Record<string, SxProps<Theme>>,
    [theme]
  );

  const isUploading = matchingImageNames.length > 0;

  const resetAndClose = () => {
    images.forEach((image) => URL.revokeObjectURL(image.thumbnail));
    mappedImagesRef.current.forEach((image) => URL.revokeObjectURL(image.thumbnail));
    setImages([]);
    setUsers([]);
    setUsersError('');
    setGridPagination({ page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE });
    setCounts({ all: 0, withPhoto: 0, without: 0 });
    setCountsLoading(false);
    setMatchFilter('unmatched');
    setSearchText('');
    setSelectedImageName(null);
    setDraggedImageName(null);
    setMatchingUserIds([]);
    setMatchingImageNames([]);
    setMapError('');
    setConfirmClose(false);
    mappedImagesRef.current.clear();
    onClose();
  };

  const handleClose = () => {
    if (isUploading) return;
    if (images.length > 0) {
      setConfirmClose(true);
      return;
    }
    resetAndClose();
  };

  const handlePageChange = (newPage: number) => {
    setGridPagination((prev) => ({ ...prev, page: newPage }));
  };

  const removeImage = (name: string) => {
    const image = images.find((img) => img.name === name);
    if (image) URL.revokeObjectURL(image.thumbnail);
    setImages((prev) => prev.filter((img) => img.name !== name));
    setSelectedImageName((prev) => (prev === name ? null : prev));
  };

  const handleSelectImage = (name: string) => {
    setSelectedImageName((prev) => (prev === name ? null : name));
  };

  const handleMapPhoto = async (officialId: number, imageName?: string) => {
    const name = imageName ?? selectedImageName;
    if (!name || !agency?.id || !user?.accessToken) return;
    const image = images.find((img) => img.name === name);
    if (!image) return;

    setSelectedImageName(null);
    setDraggedImageName(null);
    setMatchingUserIds((prev) => [...prev, officialId]);
    setMatchingImageNames((prev) => [...prev, image.name]);

    await Utils.yieldToBrowser();

    let uploadFile: File;
    try {
      uploadFile = image.source.type === 'file'
        ? image.source.file
        : await extractImageFromZip(image.source.zipFile, image.name);
    } catch {
      setMatchingUserIds((prev) => prev.filter((id) => id !== officialId));
      setMatchingImageNames((prev) => prev.filter((n) => n !== image.name));
      setMapError('Failed to save photo. Please try again.');
      return;
    }

    const { error } = await updateOfficialImage(
      user.accessToken,
      officialId,
      agency.id,
      uploadFile
    );

    if (error) {
      setMatchingUserIds((prev) => prev.filter((id) => id !== officialId));
      setMatchingImageNames((prev) => prev.filter((name) => name !== image.name));
      setMapError('Failed to save photo. Please try again.');
      return;
    }

    // Success: remember the photo against this user so unlinking can restore it,
    // then remove the image (FLIP removal animation) and silently refresh so the
    // matched user drops from the Unmapped list and the counts stay in sync.
    mappedImagesRef.current.set(officialId, image);
    setImages((prev) => prev.filter((img) => img.name !== image.name));
    setMatchingImageNames((prev) => prev.filter((name) => name !== image.name));
    await loadUsers({ silent: true });
    loadCounts({ silent: true });
    setMatchingUserIds((prev) => prev.filter((id) => id !== officialId));
  };

  // Clear a user's photo: optimistically drop the image locally, then persist by
  // setting img_url to null. The user then reads as unmapped (Unmapped tab).
  const handleUnlinkPhoto = async (official: OfficialData) => {
    if (!user?.accessToken) return;

    setUsers((prev) => prev.map((u) => (u.id === official.id ? { ...u, img_url: undefined } : u)));

    const { error } = await updateOfficial(user.accessToken, {
      id: official.id,
      img_url: null
    } as unknown as Partial<OfficialData>);

    if (error) {
      // Roll back the optimistic change and surface the failure.
      setUsers((prev) =>
        prev.map((u) => (u.id === official.id ? { ...u, img_url: official.img_url } : u))
      );
      setMapError('Failed to unlink photo. Please try again.');
      return;
    }

    // If this photo was uploaded in this session, return it to the left panel so
    // it can be re-mapped (dedup by name in case it's somehow already there).
    const restored = mappedImagesRef.current.get(official.id);
    if (restored) {
      mappedImagesRef.current.delete(official.id);
      setImages((prev) =>
        prev.some((img) => img.name === restored.name) ? prev : [...prev, restored]
      );
    }

    // Silently refresh so the now-unmapped user leaves the Mapped list and the
    // counts stay in sync, keeping current rows visible until it lands.
    await loadUsers({ silent: true });
    loadCounts({ silent: true });
  };

  const handleMatchFilterChange = (value: MatchFilter) => {
    setMatchFilter(value);
    setGridPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setGridPagination((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') handleClose();
        }}
        maxWidth={false}
        slotProps={{
          backdrop: {},
          paper: {
            style: DIALOG_PAPER_STYLE
          }
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isUploading}
          sx={styles.closeButton}
        >
          <CloseIcon sx={styles.closeIcon} />
        </IconButton>

        <DialogContent sx={styles.dialogContent}>
          <Box sx={styles.header}>
            <Typography
              variant="m"
              sx={[{
                fontWeight: "600"
              }, styles.headerTitle]}>
              Profile photos
            </Typography>
            <Typography variant="bodyMedium">
              Upload photos and map them to users. Once uploaded, drag a photo onto a user, or click to select then pick a user.
            </Typography>
          </Box>

          <Box sx={styles.panels}>
            <ImagesPanel
              images={images}
              onRemoveImage={removeImage}
              onDrop={onDrop}
              isProcessing={isProcessing}
              selectedImageName={selectedImageName}
              onSelectImage={handleSelectImage}
              matchingImageNames={matchingImageNames}
              onImageDragStart={setDraggedImageName}
              onImageDragEnd={() => setDraggedImageName(null)}
            />

            <UsersPanel
              users={users}
              loading={usersLoading}
              error={usersError}
              counts={counts}
              countsLoading={countsLoading}
              totalCount={pagination.total_count}
              page={gridPagination.page}
              pageSize={gridPagination.pageSize}
              matchFilter={matchFilter}
              onMatchFilterChange={handleMatchFilterChange}
              searchText={searchText}
              onSearchChange={handleSearchChange}
              onPageChange={handlePageChange}
              isSelecting={!!selectedImageName}
              matchingUserIds={matchingUserIds}
              draggedImageName={draggedImageName}
              onMapPhoto={handleMapPhoto}
              onUnlinkPhoto={handleUnlinkPhoto}
            />
          </Box>

          <Box sx={styles.footer}>
            <Button text="Done" onClick={handleClose} disabled={isUploading}></Button>
          </Box>
        </DialogContent>

        {mapError && (
          <Toast type="error" onClose={() => setMapError('')}>
            {mapError}
          </Toast>
        )}
      </Dialog>
      <Dialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby='discard-unmapped-photos-dialog-title'
        slotProps={{ paper: { style: { borderRadius: 8 } } }}
      >
        <DialogContent sx={{ padding: 3, paddingBottom: 0, textAlign: 'center' }}>
          <Typography>You have {images.length} photo{images.length === 1 ? '' : 's'} that {
            images.length === 1 ? "hasn't" : "haven't"
          } been mapped to a user. Closing now will discard {images.length === 1 ? 'it' : 'them'}.</Typography>
        </DialogContent>
        <DialogActions sx={{ padding: 3 }}>
          <Button mode='outlined' text='Keep editing' onClick={() => setConfirmClose(false)} />
          <Button mode='contained' text='Discard' onClick={resetAndClose} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImportImagesDialog;
